/**
 * Subscription Types & Constants
 *
 * Defines the subscription tier system for Inverio.
 * Startups have 4 tiers (Free, Starter, Standard, Premium)
 * Investors have 4 tiers (Free, Starter, Standard, Premium)
 */

// ============================================================================
// Tier Types
// ============================================================================

export type StartupTier = 'free' | 'starter' | 'standard' | 'premium';
export type InvestorTier = 'free' | 'starter' | 'standard' | 'premium';
export type SubscriptionTier = StartupTier | InvestorTier;

export type SubscriptionStatus =
  | 'active'      // Subscription is active and paid
  | 'trialing'    // In trial period
  | 'past_due'    // Payment failed, in grace period
  | 'canceled'    // Subscription has been canceled
  | 'incomplete'  // Initial payment failed
  | 'none';       // No subscription (free tier)

export type BillingInterval = 'month' | 'year';

// ============================================================================
// Subscription Interface
// ============================================================================

export interface Subscription {
  id: string;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  billingInterval: BillingInterval;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  canceledAt?: string;
  trialEnd?: string;
  // Stripe references (for future real integration)
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
  stripePriceId?: string;
}

// ============================================================================
// Tier Configuration
// ============================================================================

export interface TierConfig {
  id: StartupTier | InvestorTier;
  name: string;
  tagline: string;
  description: string;
  monthlyPrice: number; // in CHF cents
  yearlyPrice: number;  // in CHF cents (20% discount)
  features: string[];
  highlighted?: boolean;
  badge?: 'popular' | 'best-value';
}

export interface StartupTierConfig extends TierConfig {
  id: StartupTier;
  additionalTeamSeats: number;
}

export interface InvestorTierConfig extends TierConfig {
  id: InvestorTier;
}

// ============================================================================
// Startup Tier Configurations
// ============================================================================

export const STARTUP_TIERS: Record<StartupTier, StartupTierConfig> = {
  free: {
    id: 'free',
    name: 'Free',
    tagline: 'Understand your readiness',
    description: 'Basic tools to assess your investment readiness',
    monthlyPrice: 0,
    yearlyPrice: 0,
    additionalTeamSeats: 0,
    features: [
      'Pitch deck analysis (1/mo)',
      'Overall readiness score',
      'Confidence & risk rating',
      'Top 3 recommendations',
      'Basic valuation (Berkus)',
      '1 team seat',
    ],
  },
  starter: {
    id: 'starter',
    name: 'Starter',
    tagline: 'Prepare for investors',
    description: 'Full scoring and data room analysis',
    monthlyPrice: 2900, // CHF 29.00
    yearlyPrice: 27840, // CHF 278.40 (20% off)
    additionalTeamSeats: 1,
    features: [
      'Everything in Free',
      'Full 15-dimension report',
      'Data room analysis',
      'DD conversations (3/mo)',
      'Scoring refresh (1/mo)',
      'Full valuation (4 methods)',
      '2 team seats',
    ],
  },
  standard: {
    id: 'standard',
    name: 'Standard',
    tagline: 'Connect with investors',
    description: 'Access investor database and direct messaging',
    monthlyPrice: 4900, // CHF 49.00
    yearlyPrice: 47040, // CHF 470.40 (20% off)
    additionalTeamSeats: 3,
    highlighted: true,
    badge: 'popular',
    features: [
      'Everything in Starter',
      'View investor database',
      'Direct messaging (5/mo)',
      'Investor activity insights',
      'DD conversations (10/mo)',
      'Scoring refresh (3/mo)',
      '4 team seats',
    ],
  },
  premium: {
    id: 'premium',
    name: 'Premium',
    tagline: 'Accelerate fundraising',
    description: 'Automatic matching and investor feedback',
    monthlyPrice: 9900, // CHF 99.00
    yearlyPrice: 95040, // CHF 950.40 (20% off)
    additionalTeamSeats: 5,
    badge: 'best-value',
    features: [
      'Everything in Standard',
      'Automatic investor matching',
      'Investor feedback loop',
      'Priority visibility in dealflow',
      'Unlimited messaging/scoring/DD',
      '6 team seats',
    ],
  },
};

// ============================================================================
// Investor Tier Configurations
// ============================================================================

export const INVESTOR_TIERS: Record<InvestorTier, InvestorTierConfig> = {
  free: {
    id: 'free',
    name: 'Free',
    tagline: 'Centralise your inbound',
    description: 'Inbox tool to score and triage incoming pitch decks',
    monthlyPrice: 0,
    yearlyPrice: 0,
    features: [
      'Inbox (receive pitch decks)',
      'Basic scoring (50 decks)',
      'Basic filters',
      'Watchlist (10 startups)',
    ],
  },
  starter: {
    id: 'starter',
    name: 'Starter',
    tagline: 'Discover & analyse deals',
    description: 'Full due diligence and dealflow access for casual angels',
    monthlyPrice: 5900, // CHF 59.00
    yearlyPrice: 56640, // CHF 566.40 (20% off)
    features: [
      'Everything in Free',
      'Summary & full DD reports',
      'DD conversations (5/mo)',
      'Platform dealflow',
      'Direct messaging (10/mo)',
      'Scoring (100/mo)',
      'Watchlist (50)',
    ],
  },
  standard: {
    id: 'standard',
    name: 'Standard',
    tagline: 'Manage your pipeline',
    description: 'Pipeline management and portfolio tracking for active angels',
    monthlyPrice: 9900, // CHF 99.00
    yearlyPrice: 95040, // CHF 950.40 (20% off)
    highlighted: true,
    badge: 'popular',
    features: [
      'Everything in Starter',
      'Thesis fit analysis',
      'Feedback to founders',
      'Deal pipeline view',
      'Portfolio tracking',
      'Advanced filters',
      'DD conversations (20/mo)',
      'Messaging (50/mo)',
      'Scoring (300/mo)',
      'Watchlist (200)',
    ],
  },
  premium: {
    id: 'premium',
    name: 'Premium',
    tagline: 'Scale your dealflow',
    description: 'Syndicate tools and unlimited access for super angels',
    monthlyPrice: 19900, // CHF 199.00
    yearlyPrice: 191040, // CHF 1,910.40 (20% off)
    badge: 'best-value',
    features: [
      'Everything in Standard',
      'Priority deal access',
      'Auto-matching',
      'Syndicate tools',
      'Unlimited everything',
    ],
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get tier configuration by tier ID and user role
 */
export function getTierConfig(
  tier: SubscriptionTier,
  userRole: 'startup' | 'investor'
): TierConfig | undefined {
  if (userRole === 'startup') {
    return STARTUP_TIERS[tier as StartupTier];
  }
  return INVESTOR_TIERS[tier as InvestorTier];
}

/**
 * Get all tiers for a user role
 */
export function getTiersForRole(
  userRole: 'startup' | 'investor'
): TierConfig[] {
  if (userRole === 'startup') {
    return Object.values(STARTUP_TIERS);
  }
  return Object.values(INVESTOR_TIERS);
}

/**
 * Check if a tier is higher than another
 */
export function isTierHigher(
  tier1: SubscriptionTier,
  tier2: SubscriptionTier,
  userRole: 'startup' | 'investor'
): boolean {
  const order: Record<string, number> = { free: 0, starter: 1, standard: 2, premium: 3 };

  return (order[tier1] ?? 0) > (order[tier2] ?? 0);
}

/**
 * Get the default/lowest tier for a user role
 */
export function getDefaultTier(userRole: 'startup' | 'investor'): SubscriptionTier {
  return 'free';
}

/**
 * Check if subscription is in a good standing
 */
export function isSubscriptionActive(status: SubscriptionStatus): boolean {
  return status === 'active' || status === 'trialing';
}

/**
 * Check if subscription needs attention (payment issue)
 */
export function needsAttention(status: SubscriptionStatus): boolean {
  return status === 'past_due' || status === 'incomplete';
}
